<template>
  <modal v-if="!loading" class="style-modal-wide-large" @close-modal="close">
    <main class="create-game-modal">
      <div class="modal-container">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-content">
            <font-awesome-icon :icon="faPlus" class="header-icon" />
            <div>
              <h1 class="modal-title">Create a Market</h1>
              <p class="modal-subtitle">Set up a custom betting market</p>
            </div>
          </div>
          <router-link to="/rules" class="rules-link">
            <font-awesome-icon :icon="faCircleQuestion" class="mr-1" />
            <span class="rules-text">Rules</span>
          </router-link>
        </div>

        <!-- Form -->
        <div class="form-container">
          <!-- Sport & League Row -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Sport</label>
              <div class="select-wrapper">
                <select
                  id="sports_value"
                  v-model="sports_value"
                  class="form-select"
                >
                  <option disabled :value="''" hidden>Select Sport</option>
                  <option
                    v-for="(sport, index) in sportsList"
                    :key="index"
                    :value="sport"
                  >
                    {{ sport.toUpperCase() }}
                  </option>
                </select>
                <font-awesome-icon :icon="faChevronDown" class="select-arrow" />
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">League</label>
              <div class="form-input form-input--disabled">
                {{ displayLeague }}
              </div>
            </div>
          </div>

          <!-- Event & Start Time Row -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Event Description</label>
              <input
                v-model="home_info"
                type="text"
                class="form-input"
                placeholder="e.g., 2024 Championship Final"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Start Time</label>
              <input
                v-model="date_game"
                type="text"
                class="form-input"
                placeholder="YYYY-MM-DD hh:mm:ss AM/PM"
              />
            </div>
          </div>

          <!-- Teams Row -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Away Team / Player</label>
              <input
                v-model="away_team_name"
                type="text"
                class="form-input"
                placeholder="Enter away team name"
                @input="away_abrev = away_team_name"
              />
              <span class="form-hint">Displays on top</span>
            </div>
            <div class="form-group">
              <label class="form-label">Home Team / Player</label>
              <input
                v-model="home_team_name"
                type="text"
                class="form-input"
                placeholder="Enter home team name"
                @input="home_abrev = home_team_name"
              />
              <span class="form-hint">Displays on bottom</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn-cancel" @click="close">
            Cancel
          </button>
          <button
            :disabled="buttonDisabled"
            class="btn-create"
            @click="createGame"
          >
            <font-awesome-icon :icon="faPlus" class="mr-2" />
            Create Market
          </button>
        </div>
      </div>
    </main>
  </modal>
  <modal v-else class="modal-wrap" @close-modal="close">
    <spinner />
  </modal>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import dayjs from 'dayjs';
import { useAPI } from '@/composables/useAPI';
import { creationGameSuccess, creationGameFail } from '@/core/notifications';
import { checkAuthAndShowLogin } from '@/core/authUtils';
import { emitter } from '@/core/emitter';

import Spinner from '@/components/global/Spinner.vue';
import Modal from '@/components/global/WideModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons/faCircleQuestion';

const props = defineProps({
  initialSport: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['closeModal']);

const date_game = ref('');
const home_team_name = ref('');
const away_team_name = ref('');
const away_abrev = ref('');
const home_abrev = ref('');
const home_info = ref('');
const away_info = ref('');
const sports = ref({});
const loading = ref(false);
const sports_value = ref('custom');

const buttonDisabled = computed(() => {
  return !!(
    date_game.value === '' ||
    home_team_name.value === '' ||
    away_team_name.value === ''
  );
});

const sportsList = computed(() => {
  return Object.keys(sports.value);
});

const displayLeague = computed(() => {
  if (!sports_value.value || sports_value.value === 'custom') {
    return 'CUSTOM';
  }
  return `CUSTOM-${sports_value.value.toUpperCase()}`;
});

onMounted(async () => {
  await getSportLeagues();
  const iniDate = dayjs();
  date_game.value = iniDate.add(1, 'day').format('YYYY-MM-DD hh:mm:ss A');
  
  if (props.initialSport) {
    sports_value.value = props.initialSport.toLowerCase();
  }
});

async function getSportLeagues() {
  try {
    loading.value = true;
    const { get } = useAPI('/exchange/getSportLeagues');
    const response = await get();
    sports.value = response.sportsAvailable.custom
      ? response.sportsAvailable
      : { custom: ['CUSTOM'], ...response.sportsAvailable };
  } catch (error) {
    console.log('error getting sport leagues');
  } finally {
    loading.value = false;
  }
}

async function createGame() {
  if (!checkAuthAndShowLogin()) {
    return;
  }
  const hasEdgeWhitespace = (v) => typeof v === 'string' && v !== v.trim();
  if (
    hasEdgeWhitespace(home_team_name.value) ||
    hasEdgeWhitespace(away_team_name.value)
  ) {
    emitter.emit('showAlert', {
      type: 'submit-edit-order',
      shown: true,
      status: 'danger',
      title: 'Team names cannot start or end with a space',
      detail: 'Please remove leading or trailing spaces from team names.',
    });
    return;
  }
  if (home_team_name.value.trim().toLowerCase() === away_team_name.value.trim().toLowerCase()) {
    emitter.emit('showAlert', {
      type: 'submit-edit-order',
      shown: true,
      status: 'danger',
      title: 'Home and Away must be different',
      detail: 'Please enter distinct names for each side of the market.',
    });
    return;
  }
  loading.value = true;
  let eventName = `${home_team_name.value}-VS-${away_team_name.value}`;
  eventName = eventName.replace(' ', '-').toUpperCase();
  const cheapDataUID = `${home_team_name.value}-${away_team_name.value}-${dayjs(date_game.value).format('YYYY-MM-DD-hh-mm-sS')}`;
  const structuredGame = {
    participants: [
      {
        longName: home_team_name.value,
        shortName: home_team_name.value,
      },
      {
        longName: away_team_name.value,
        shortName: away_team_name.value,
      },
    ],
    league:
      sports_value.value === 'custom'
        ? 'CUSTOM'
        : `CUSTOM-${sports_value.value.toUpperCase()}`,
    cheapDataUID,
    sport:
      sports_value.value !== '' ? sports_value.value.toLowerCase() : 'custom',
    eventName,
    homeRotationNumber: '',
    awayRotationNumber: '',
    mainSpread: null,
    mainTotal: null,
    homePitcher: home_info.value,
    awayPitcher: home_info.value,
    source: 'admin',
    start: date_game.value,
  };
  try {
    const { request } = useAPI('/exchange/createGame');
    await request({ structuredGame });
    creationGameSuccess(eventName);
  } catch (error) {
    console.error(error);
    creationGameFail(error, eventName);
  } finally {
    loading.value = false;
    close();
  }
}

function close() {
  sports_value.value = '';
  emit('closeModal');
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.create-game-modal {
  padding: 0;
  max-height: 90vh;
  overflow-y: auto;
  
  @media screen and (max-width: 576px) {
    max-height: 85vh;
  }
}

.modal-container {
  background: var(--white_01);
  border-radius: 16px;
  overflow: hidden;
  
  @media screen and (max-width: 576px) {
    border-radius: 12px;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border-bottom: 1px solid var(--grey-1200-1);
  
  [theme='night'] & {
    background: linear-gradient(135deg, rgba(150, 156, 203, 0.1) 0%, rgba(134, 144, 219, 0.15) 100%);
  }
  
  @media screen and (max-width: 576px) {
    padding: 12px 16px;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media screen and (max-width: 576px) {
    gap: 10px;
  }
}

.header-icon {
  font-size: 20px;
  color: var(--color-primary_05);
  background: var(--white_01);
  padding: 10px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media screen and (max-width: 576px) {
    font-size: 16px;
    padding: 8px;
    border-radius: 8px;
  }
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-primary_01);
  margin: 0;
  
  @media screen and (max-width: 576px) {
    font-size: 1rem;
  }
}

.modal-subtitle {
  font-size: 0.8rem;
  color: var(--grey-2200);
  margin: 2px 0 0 0;
  
  @media screen and (max-width: 576px) {
    font-size: 0.75rem;
    display: none;
  }
}

.rules-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--color-primary_05);
  text-decoration: none;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--white_01);
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--grey-1600);
    color: var(--color-primary_01);
  }
  
  @media screen and (max-width: 576px) {
    padding: 6px 10px;
    font-size: 0.75rem;
    
    .rules-text {
      display: none;
    }
  }
}


.form-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media screen and (max-width: 576px) {
    padding: 16px;
    gap: 12px;
  }
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media screen and (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-primary_01);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media screen and (max-width: 576px) {
    font-size: 0.7rem;
  }
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--grey-1200-1);
  border-radius: 8px;
  background: var(--white_01);
  color: var(--color-primary_01);
  font-size: 14px;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: var(--grey-2200);
    font-size: 13px;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary_05);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
  
  &--disabled {
    background: var(--grey-1600);
    color: var(--color-primary_01);
    font-weight: 600;
    display: flex;
    align-items: center;
    cursor: not-allowed;
  }
  
  @media screen and (max-width: 576px) {
    height: 40px;
    font-size: 13px;
    
    &::placeholder {
      font-size: 12px;
    }
  }
}

.form-hint {
  font-size: 0.7rem;
  color: var(--grey-2200);
  margin-top: 2px;
}

.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  height: 44px;
  padding: 0 36px 0 12px;
  border: 1px solid var(--grey-1200-1);
  border-radius: 8px;
  background: var(--white_01);
  color: var(--color-primary_01);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary_05);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  }
  
  @media screen and (max-width: 576px) {
    height: 40px;
    font-size: 13px;
  }
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: var(--grey-2200);
  pointer-events: none;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: var(--grey-1600);
  border-top: 1px solid var(--grey-1200-1);
  
  @media screen and (max-width: 576px) {
    padding: 12px 16px;
    flex-direction: column-reverse;
    gap: 8px;
  }
}

.btn-cancel {
  padding: 10px 20px;
  border: 1px solid var(--grey-1200-1);
  border-radius: 8px;
  background: var(--white_01);
  color: var(--color-primary_01);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--grey-1600);
    border-color: var(--color-primary_05);
  }
  
  @media screen and (max-width: 576px) {
    width: 100%;
    padding: 12px;
  }
}

.btn-create {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media screen and (max-width: 576px) {
    width: 100%;
    padding: 12px;
  }
}
</style>
